// src/shared/dashboard/shell/nav.ts
//
// Pure nav logic for the global AppShell. The ONE nav config is each vertical's
// `dashboard.sidebar`: a flat item becomes a top pill whose icon-rail is just
// itself; an item with `children` becomes a pill whose rail is those children
// (the only 2-level vertical today is mental-health-education). No JSX here so
// this stays trivially testable.
import { isArchivedSimulationPath } from "@/shared/lib/archivedSimulationRoutes";
import type { FeatureFlags, ShellChrome, SidebarItem } from "@/verticals/types";

/** A resolved section: the top pill (a SidebarItem) plus the contextual icon-rail
 *  entries shown while it's active. */
export interface NavSection extends SidebarItem {
  rail: SidebarItem[];
}

const norm = (p: string) =>
  p.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";

/** The feature flag a route is gated by (mirrors the FeatureGate guards in
 *  App.tsx) so the shell never renders a pill/rail entry that would 404. */
function pathFeature(path: string): keyof FeatureFlags | null {
  const p = norm(path);
  if (p === "/schedule") return "scheduling";
  if (p === "/messages") return "messages";
  if (p === "/courses" || p === "/program" || p.startsWith("/course"))
    return "courses";
  if (p === "/clients" || p === "/notes" || p === "/files")
    return "clientSessions";
  if (p === "/calendar/import") return "calendarImport";
  if (p === "/supervisor") return "supervisor";
  return null;
}

export function isPathEnabled(path: string, features: FeatureFlags): boolean {
  if (isArchivedSimulationPath(path)) return false;
  const f = pathFeature(path);
  return f === null || features[f] === true;
}

/** True when `pathname` is `target` or a sub-route of it. */
export function matchesPath(target: string, pathname: string): boolean {
  const cur = norm(pathname);
  return cur === norm(target) || cur.startsWith(norm(target) + "/");
}

/** True when `pathname` belongs to a nav item: its own path, a sub-route of it,
 *  or one of its declared `aliasPaths` (e.g. `/course/:id` belongs to `/courses`).
 *  This is what keeps a course detail page lit under the Learn tab. */
export function itemMatchesPath(item: SidebarItem, pathname: string): boolean {
  return (
    matchesPath(item.path, pathname) ||
    (item.aliasPaths ?? []).some((a) => matchesPath(a, pathname))
  );
}

/** Shared baseline for every vertical's shell chrome. A vertical overrides any
 *  field from ONE place — its `dashboard.shellChrome` (see verticals/types.ts):
 *  - hideLeftMenu: full-screen surfaces (the live session) and the mentor surface
 *    itself (/supervisor) carry their own header/controls, so no left rail.
 *  - hideRightSidebar: pages that are the mentor surface, the focused session, or
 *    simply don't want the extra 330px Emery column. */
export const DEFAULT_SHELL_CHROME: Required<ShellChrome> = {
  hideLeftMenu: ["/supervisor"],
  hideRightSidebar: ["/courses", "/supervisor", "/profile", "/exercises"],
};

/** Merge a vertical's override onto the baseline. Replacing a list replaces it
 *  wholesale (predictable); omitting it keeps the default. */
export function resolveShellChrome(
  override?: ShellChrome,
): Required<ShellChrome> {
  return {
    hideLeftMenu: override?.hideLeftMenu ?? DEFAULT_SHELL_CHROME.hideLeftMenu,
    hideRightSidebar:
      override?.hideRightSidebar ?? DEFAULT_SHELL_CHROME.hideRightSidebar,
  };
}

/** Whether this route shows the left icon rail, per the resolved chrome. */
export function showLeftMenu(
  pathname: string,
  chrome: Required<ShellChrome>,
): boolean {
  return !chrome.hideLeftMenu.some((p) => matchesPath(p, pathname));
}

/** Whether this route shows the right Emery mentor panel, per the resolved chrome.
 *  (The shell additionally gates this on the vertical's `supervisor` feature.) */
export function showRightSidebar(
  pathname: string,
  chrome: Required<ShellChrome>,
): boolean {
  return !chrome.hideRightSidebar.some((p) => matchesPath(p, pathname));
}

/** Derive the shell's sections from a vertical's sidebar, dropping any entry
 *  whose route is gated off by the vertical's features. */
export function toSections(
  sidebar: SidebarItem[],
  features: FeatureFlags,
): NavSection[] {
  return sidebar
    .filter((item) => isPathEnabled(item.path, features))
    .map((item) => ({
      ...item,
      rail: (item.children ?? [item]).filter((r) =>
        isPathEnabled(r.path, features),
      ),
    }));
}

/** The active section: the one whose own path matches, else the one owning a
 *  rail entry that matches, else the first. */
export function resolveActiveSection(
  sections: NavSection[],
  pathname: string,
): NavSection | undefined {
  return (
    sections.find((s) => itemMatchesPath(s, pathname)) ??
    sections.find((s) => s.rail.some((r) => itemMatchesPath(r, pathname))) ??
    sections[0]
  );
}
